<template>
	<Teleport to="html">
		<EditUserModal
			:show="userToEdit != null"
			:user-to-edit="userToEdit"
			@dismiss="onEditUserDismiss"
			@show-discard-changes-modal="onShowDiscardChangesModal"
			@show-toggle-activate-account-modal="onShowToggleActivateAccountModal"
		/>
	</Teleport>

	<Teleport to="html">
		<EditUserPermissionModal
			:show="permissionToEdit != null"
			:permission-to-edit="permissionToEdit"
			@dismiss="onEditPermissionDismiss"
			@show-discard-changes-modal="onShowDiscardChangesModal"
			@show-toggle-activate-permission-modal="onShowToggleActivatePermissionModal"
			:points-of-contact="pointsOfContact"
			:orgRoles="organizationRoles"
		/>
	</Teleport>

	<Teleport to="html">
		<MultiButtonModal
			:show="showDiscardChangesModal"
			icon="alert"
			title="Discard Changes"
			message="Are you sure? Any unsaved changes will be lost."
			:buttons="[
									new ModalButtonData(
										'Cancel', hideDiscardChangesModal, ButtonType.Default, ButtonSize.Small
									),
									new ModalButtonData(
										'Yes', () => { onDiscardChanges() }, ButtonType.Default, ButtonSize.Small
									)
								]"
		/>
	</Teleport>

	<Teleport to="html">
		<MultiButtonModal
			:show="showToggleActivateAccountModal"
			icon="alert"
			:title="toggleActivateAccountIsActivate ? 'Activate Account' : 'Disable Account'"
			message="Are you sure?"
			:buttons="[
									new ModalButtonData(
										'Cancel', hideToggleActivateAccountModal, ButtonType.Default, ButtonSize.Small
									),
									new ModalButtonData(
										toggleActivateAccountIsActivate ? 'Activate' : 'Disable', () => { onToggleActivateAccount() }, ButtonType.Default, ButtonSize.Small
									)
								]"
		/>
	</Teleport>

	<Teleport to="html">
		<MultiButtonModal
			:show="showToggleActivatePermissionModal"
			icon="alert"
			:title="toggleActivatePermissionIsActivate ? 'Activate Permission' : 'Deactivate Permission'"
			message="Are you sure?"
			:buttons="[
									new ModalButtonData(
										'Cancel', hideToggleActivatePermissionModal, ButtonType.Default, ButtonSize.Small
									),
									new ModalButtonData(
										toggleActivatePermissionIsActivate ? 'Activate' : 'Deactivate', () => { onToggleActivatePermission() }, ButtonType.Default, ButtonSize.Small
									)
								]"
		/>
	</Teleport>

	<Teleport to="html">
		<InviteUsersModal :show="showInviteModal" @onDismiss="showInviteModal = false" />
	</Teleport>

	<Teleport to="html">
		<UploadUserInvitesModal :show="showUserInviteUploadModal" @evCloseModal="onCloseModal" />
	</Teleport>

	<Teleport to="html">
		<UploadUserOrganizationsModal :show="showUserOrgInviteModal" @evCloseModal="onCloseModal" />
	</Teleport>

	<AdminLayout>
		<div class="flex flex-row mb-4">
			<p class="admin-center-subtitle">Users</p>
			<SubmitButton v-if="!showImportMenu" label="Invite" @click="onInviteClicked" size="small" class="mr-2"/>
			<ActionMenu
				v-if="showImportMenu"
				:items="getMenuItems()"
				@onActionMenuItemSelected="onActionMenuItemSelected"
			></ActionMenu>
		</div>

		<PortalDataTable
			ref="table"
			:endpoints="endpoints"
			@on-data-loaded="onDataLoaded"
		>

			<template #advanced-search>
				<div class="flex flex-row mr-1 mt-1 justify-end">
					<div class="flex w-full justify-end overflow-hidden">
						<Popper :content="`Only show users that haven't logged in for over 30 days or no value set.`" hover arrow placement="bottom-start">
							<CheckBox input-id="cbx_no_login" label="Show inactive for 30+ days" label-location="left" class="my-2" :checked="false" v-model="showOldLogins" @checked-changed="toggleOldLogins" />
						</Popper>
					</div>
				</div>
			</template>

			<template #user_name="data">
				<p class="userpilot-mask endpoint-clickable-text whitespace-pre-wrap lg:whitespace-nowrap" @click.prevent="onNameClicked(data.value)">
					{{ data.value.user_name }}
				</p>
			</template>

			<template #email="data">
				<div class="flex flex-col w-fit space-x-0 space-y-1 2xl:flex-row 2xl:space-x-1 2xl:space-y-0">
					<span class="userpilot-mask">{{ data.value.email }}</span>
					<span v-if="data.value.tfa_status === 'pending'" class="admin-center-login-status-tag-pending whitespace-pre-wrap lg:whitespace-nowrap">2FA Pending</span>
					<span v-if="data.value.tfa_status === 'expired'" class="admin-center-login-status-tag-tfa whitespace-pre-wrap lg:whitespace-nowrap">2FA Expired</span>
				</div>
			</template>

			<template #custom="data">
				<div class="flex flex-col">
							<span v-for="organization in data.value.organizations" :key="organization.id" class="text-wrap">
								<span :class="{'endpoint-clickable-text' : canEditOrganization(organization.id) }" @click.prevent="onOrganizationClicked(data.value, organization)">{{ organization.organization_name + (organization.organization_name_dba ? " (" + organization.organization_name_dba + ")" : "") }}</span>
								<span v-if="organization.user_role"> / {{ organization.user_role }}</span>
							</span>
					<div class="button-outline w-fit flex flex-row mt-2 cursor-pointer rounded-md !px-2 !py-1" @click="showAddOrgModal(data.value.id, data.value.all_org_ids)">
						<div class="mt-1 mr-2">
							<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 23 24">
								<title>Add</title>
								<desc>Click to Add Organization</desc>
								<path d="M15.325 11.137h-3V8.175c0-.45-.375-.825-.862-.825-.45 0-.825.375-.825.862v2.963H7.675c-.45 0-.825.375-.825.862 0 .45.375.825.863.825h2.962v2.963c0 .45.375.825.863.825.45 0 .825-.375.825-.863v-2.962h2.962c.45 0 .825-.375.825-.863a.83.83 0 0 0-.825-.825"/>
								<path d="M11.5.675A11.315 11.315 0 0 0 .175 12c0 6.262 5.1 11.362 11.362 11.362S22.9 18.262 22.9 12C22.863 5.737 17.763.675 11.5.675m0 21c-5.325 0-9.637-4.35-9.637-9.675A9.634 9.634 0 0 1 11.5 2.362c5.325 0 9.675 4.313 9.675 9.638s-4.35 9.675-9.675 9.675"/>
							</svg>
						</div>
						<div>
							Add New Organization
						</div>
					</div>
				</div>
			</template>

			<template #last_login_at="data">
				{{ Format.date(data.value.last_login_at) }}
			</template>

			<template #deleted_at="data">
				<div class="flex w-full justify-center whitespace-nowrap">
					<EndpointTableListBox
						v-if="canEditStatus"
						:record-id="data.value.id"
						:items="statusColumnData"
						v-model="data.value.status"
						:disabled="!canEditStatus || data.value.id === authStore?.getAuth?.user().id"
						:showSelectedCheckmark="false"
						width="w-26"
						@onItemSelected="onStatusItemSelected"
						:displayed-item-c-s-s="{ 'Disabled': '!bg-disabled-subtle !text-label', 'Active': '!bg-primary-lighter !text-label'}"
					/>
					<div v-else class="rounded px-2 py-1 text-center" :class="{ 'bg-primary-lighter': data.value.status.text === 'Active', 'bg-disabled-subtle': data.value.status.text === 'Disabled' }">{{ data.value.status.text }}</div>
				</div>
			</template>
		</PortalDataTable>
	</AdminLayout>
</template>

<script setup lang="js">
import AdminLayout from "@/layouts/AdminLayout.vue";
import { computed, onMounted, ref } from 'vue'
import EditUserModal from "@/views/Admin/User/EditUserModal.vue";
import MultiButtonModal from "@/components/Alerts/MultiButtonModal.vue";
import EditUserPermissionModal from "@/views/Admin/User/EditUserPermissionModal.vue";
import { userAdminApi } from "@/api/User/UserAdminApi.js";
import InviteUsersModal from "@/views/Admin/User/InviteUsersModal.vue";
import { useOrganizationStore } from '@/stores/organizationStore.js'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import EndpointTableListBox from '@/components/Tables/EndpointTableListBox.vue'
import { useAuthStore } from '@/stores/authStore.js'
import { Toaster } from '@/models/utils/Toaster.js'
import UploadUserInvitesModal from '@/views/Admin/User/UploadUserInvitesModal.vue'
import UploadUserOrganizationsModal from '@/views/Admin/User/UploadUserOrganizationsModal.vue'
import ActionMenu from '@/components/Forms/ActionMenu.vue'
import PortalDataTable from '@/components/DataTable/PortalDataTable.vue'
import { DataTableEndpointDefinition } from '@/models/datatable/DataTableEndpointDefinition.js'
import { Format } from '@/models/utils/FormatUtils.js'
import CheckBox from '@/components/Forms/CheckBox.vue'

const orgStore = useOrganizationStore()

const userToEdit = ref(null);
const permissionToEdit = ref(null);
const showDiscardChangesModal = ref(false);
const showDiscardChangesModalCallback = ref(null);
const showToggleActivateAccountModal = ref(false);
const toggleActivateAccountIsActivate = ref(false);
const showToggleActivateAccountModalCallback = ref(null);
const showToggleActivatePermissionModal = ref(false);
const toggleActivatePermissionIsActivate = ref(false);
const showToggleActivatePermissionModalCallback = ref(null);
const showInviteModal = ref(false);
const showUserOrgInviteModal = ref(false);
const showUserInviteUploadModal = ref(false);

const isLoadingPointsOfContact = ref(false);
const pointsOfContact = ref(null);

const organizationRoles = ref([]);
const authStore = useAuthStore()
const canEditStatus = ref(authStore.isPortalAdmin)
const showImportMenu = ref((authStore.isDeveloper && authStore.isPortalAdmin))
const editableOrgIds = ref(authStore.getEditableOrgs)
const toaster = new Toaster();
const showOldLogins = ref(false);

const statusColumnData = computed(() =>
{
	let items = [];

	items.push({ id: 1, text:'Active' })
	items.push({ id: 2, text:'Disabled' })

	return items;
})

const table = ref(null)
const columns = [
	//If you add search able columns you need to update the whitelist in processUserFilter
	{ field: "user_name", title: "Name", sort: true, width: '20%', search:true  },
	{ field: "email", title: "Email", sort: true, width: '20%', search:true },
	{ field: "custom", title: "Permissions", sort: false },
	{ field: "last_login_at", title: "Last Login", sort: true, width: '1%' },
	{ field: "deleted_at", title: "Status", sort: false, width: '1%' },
	{ field: "phone_number", hide:true, search:true },
	{ field: "org_names", hide:true, search:true },
]

const advancedSearchParams = computed(() => {
	return [
		{
			"id": 'old_logins',
			"value": showOldLogins.value
		}
	]
})

const endpoints = [
	new DataTableEndpointDefinition(
		{
			endpointRoute: 'users/users',
			initialSortColumn: 'id',
			searchPlaceholder: 'Search by name, email or organization',
			advancedSearchParams: advancedSearchParams,
			columns: columns,
			fullTextSearch: false,
		}
	)
]

onMounted(() => {
	getPointsOfContact();

	//Sets table in store so we can reload table after adding
	orgStore.setUsersTable(table);
});

/**
 * Checks if the user can edit the organization for the user
 * @param p_org_id
 * @returns {boolean}
 */
function canEditOrganization(p_org_id)
{
	if (authStore.isPortalAdmin) return true;

	if (editableOrgIds.value.includes(p_org_id))
		return true;

	return false
}

function onDataLoaded(data)
{
	organizationRoles.value = data["org_roles"];
}

function getPointsOfContact() {
	userAdminApi.getPointsOfContact(
		(users) => {
			let userMap = new Map();

			userMap.set(null, ""); //Blank value

			Object.values(users).forEach((user) => {
				const { id, given_name, family_name } = user;
				userMap.set(id, `${given_name} ${family_name}`);
			});

			pointsOfContact.value = userMap;
			isLoadingPointsOfContact.value = false;
		},
		(p_err) => {
			isLoadingPointsOfContact.value = false;
			p_err.showErrorBanner()
		},
	);
}

function getMenuItems()
{
	const items = [];

	items.push({ id: 1, text: 'Invite', action:'invite' });
	items.push({ id: 2, text: 'Upload User Invites', action:'user_invites' });
	items.push({ id: 3, text: 'Upload User Orgs', action:'add_orgs'});

	return items
}

function onEditUserDismiss(reload) {
	userToEdit.value = null;

	if (reload) {
		toaster.success("User successfully updated!")
		doReload()
	}
}

function onEditPermissionDismiss(reload) {
	permissionToEdit.value = null;

	if (reload) {
		toaster.success("User permissions successfully updated!")
		doReload()
	}
}

function onShowDiscardChangesModal(callback) {
	showDiscardChangesModal.value = true;
	showDiscardChangesModalCallback.value = callback;
}

function onShowToggleActivateAccountModal(isActivate, callback) {
	showToggleActivateAccountModal.value = true;
	toggleActivateAccountIsActivate.value = isActivate;
	showToggleActivateAccountModalCallback.value = callback;
}

function onShowToggleActivatePermissionModal(isActivate, callback) {
	showToggleActivatePermissionModal.value = true;
	toggleActivatePermissionIsActivate.value = isActivate;
	showToggleActivatePermissionModalCallback.value = callback;
}

function onDiscardChanges() {
	hideDiscardChangesModal();
	showDiscardChangesModalCallback.value(true);
	showDiscardChangesModalCallback.value = null;
}

function onToggleActivateAccount() {
	hideToggleActivateAccountModal()
	showToggleActivateAccountModalCallback.value(true);
	toggleActivateAccountIsActivate.value = false;
	showToggleActivateAccountModalCallback.value = null;
}

function onToggleActivatePermission() {
	hideToggleActivatePermissionModal();
	showToggleActivatePermissionModalCallback.value(true);
	toggleActivatePermissionIsActivate.value = false;
	showToggleActivatePermissionModalCallback.value = null;
}

function onInviteClicked() {
	showInviteModal.value = true;
}

function onActionMenuItemSelected(p_menu_item)
{
	switch (p_menu_item.action)
	{
		case "invite":
			onInviteClicked()
			break;
		case "user_invites":
			showUserInviteUploadModal.value = true;
			break;
		case "add_orgs":
			showUserOrgInviteModal.value = true;
			break;
		default:
			alert("Not yet implemented")
			break;
	}
}

function onNameClicked(user) {
	userToEdit.value = user;
}

function onOrganizationClicked(record, organization)
{
	if (!canEditOrganization(organization.id)) return;
	organization.email = record.email;
	organization.phone_number = record.phone_number;
	organization.given_name = record.given_name;
	organization.family_name = record.family_name;
	permissionToEdit.value = organization;
}

/**
 * Show Add Organization Modal
 * @param p_user_id {number} User ID of user to add for
 * @param p_existing_orgs {Array} List of existing organizations to create list of existing org keys
 * @author AJL (2025-10-15)
 */
function showAddOrgModal(p_user_id, p_existing_orgs)
{
	//const org_keys = (p_existing_orgs && p_existing_orgs.length > 0) ? p_existing_orgs.map(org => org.id) : [];
	orgStore.showAdminAddOrgToUserModal(p_user_id, p_existing_orgs);
}


function toggleOldLogins() {
	table.value.loadData()
}

function doReload() {
	reloadTable()
}

function reloadTable()
{
	if (table.value) {
		table.value.loadData()
	}
	else {
		location.reload()
	}
}

function hideDiscardChangesModal()
{
	showDiscardChangesModal.value = false;
}

function hideToggleActivateAccountModal()
{
	showToggleActivateAccountModal.value = false;
}


function hideToggleActivatePermissionModal()
{
	showToggleActivatePermissionModal.value = false;
}


function onStatusItemSelected(p_item)
{
	if (p_item.text === "Active")
	{
		restore(p_item);
	}
	else
	{
		deactivate(p_item);
	}
}

function restore(p_item)
{
	userAdminApi.restore(p_item.record_id, () =>
	{
		toaster.success("User Activated")
		doReload()
	}, (e) =>
	{
		e.showErrorBanner()
	})
}

function deactivate(p_item)
{
	userAdminApi.delete(p_item.record_id, () =>
	{
		toaster.success("User Disabled")
		doReload()
	}, (e) =>
	{
		e.showErrorBanner()
	})
}

function toggleModal(p_type, p_show_eh)
{
	switch(p_type)
	{
		case "user_org_upload":
			showUserOrgInviteModal.value = p_show_eh
			break;
		case "user_invite_upload":
			showUserInviteUploadModal.value = p_show_eh
			break;
	}
}

function onCloseModal(p_type, p_reload_eh)
{
	toggleModal(p_type, false)

	if (p_reload_eh) {
		reloadTable()
	}
}

</script>
